import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Presupuesto = { id: number; periodo: string; concepto: string; valorPresupuestado: number; valorReal: number };
type Kpi = { id: number; nombre: string; formula: string | null; valorActual: number; unidad: string; fechaCalculo: string };
type FlujoCaja = { id: number; fecha: string; tipo: string; concepto: string; valor: number };
type Riesgo = { id: number; descripcion: string; probabilidad: string; impacto: string; estado: string; controles: { id: number; descripcionControl: string; responsable: string | null }[] };
type Hallazgo = { id: number; modulo: string; descripcion: string; severidad: string; estado: string; seguimientos: { id: number; fecha: string; accion: string; responsable: string | null }[] };

function formatValor(v: number, unidad: string) {
  if (unidad === "moneda") return `$${Math.round(v).toLocaleString()}`;
  if (unidad === "porcentaje") return `${v.toFixed(1)}%`;
  return v.toLocaleString();
}

export function Gerencial() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("gerencial:crear");
  const puedeEditar = tienePermiso("gerencial:editar");

  const [tab, setTab] = useState("kpis");
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [kpis, setKpis] = useState<Kpi[]>([]);
  const [flujo, setFlujo] = useState<FlujoCaja[]>([]);
  const [riesgos, setRiesgos] = useState<Riesgo[]>([]);
  const [hallazgos, setHallazgos] = useState<Hallazgo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [p, k, f, r, h] = await Promise.all([
        api.get("/gerencial/presupuestos"), api.get("/gerencial/kpis"), api.get("/gerencial/flujo-caja"),
        api.get("/gerencial/riesgos"), api.get("/gerencial/hallazgos"),
      ]);
      setPresupuestos(p); setKpis(k); setFlujo(f); setRiesgos(r); setHallazgos(h);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar gerencial.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  return (
    <Shell titulo="Gerencial" breadcrumb="Gerencial">
      <p className="text-sm text-textmuted mb-4">
        Presupuestos, indicadores de gestión, flujo de caja, y control interno (riesgos con sus controles mitigantes,
        hallazgos con su seguimiento) — el nivel de auditoría de gestión, distinto de la bitácora técnica de Core.
      </p>
      <Tabs
        tabs={[
          { key: "kpis", label: "Indicadores (KPI)" },
          { key: "presupuestos", label: "Presupuestos" },
          { key: "flujo", label: "Flujo de caja" },
          { key: "riesgos", label: "Riesgos" },
          { key: "hallazgos", label: "Hallazgos" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "kpis" && <TabKpis kpis={kpis} puedeCrear={puedeCrear} recargar={cargar} />}
          {tab === "presupuestos" && <TabPresupuestos presupuestos={presupuestos} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />}
          {tab === "flujo" && <TabFlujo flujo={flujo} puedeCrear={puedeCrear} recargar={cargar} />}
          {tab === "riesgos" && <TabRiesgos riesgos={riesgos} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />}
          {tab === "hallazgos" && <TabHallazgos hallazgos={hallazgos} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />}
        </>
      )}
    </Shell>
  );
}

function TabKpis({ kpis, puedeCrear, recargar }: { kpis: Kpi[]; puedeCrear: boolean; recargar: () => void }) {
  const [recalculando, setRecalculando] = useState(false);
  const recalcular = async () => {
    setRecalculando(true);
    try { await api.post("/gerencial/kpis/recalcular", {}); recargar(); } catch { /* noop */ } finally { setRecalculando(false); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={recalcular} disabled={recalculando}>{recalculando ? "Recalculando..." : "↻ Recalcular con datos reales"}</button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.id} className="card p-4">
            <div className="text-xs text-textmuted">{k.nombre}</div>
            <div className="text-xl font-bold text-slate-800 mt-1">{formatValor(k.valorActual, k.unidad)}</div>
            {k.formula && <div className="text-[10px] text-textmuted mt-2 font-mono">{k.formula}</div>}
          </div>
        ))}
        {kpis.length === 0 && <div className="col-span-4 text-sm text-textmuted">Sin indicadores calculados todavía.</div>}
      </div>
    </div>
  );
}

function TabPresupuestos({ presupuestos, puedeCrear, puedeEditar, recargar }: { presupuestos: Presupuesto[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void }) {
  const [periodo, setPeriodo] = useState("");
  const [concepto, setConcepto] = useState("");
  const [valorPresupuestado, setValorPresupuestado] = useState("");
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("/gerencial/presupuestos", { periodo, concepto, valorPresupuestado: Number(valorPresupuestado) });
      setPeriodo(""); setConcepto(""); setValorPresupuestado("");
      recargar();
    } catch { /* noop */ } finally { setEnviando(false); }
  };

  const eliminar = async (id: number) => {
    try { await api.del(`/gerencial/presupuestos/${id}`); recargar(); } catch { /* noop */ }
  };

  return (
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead><tr className="border-b border-borderc"><th className="table-th">Periodo</th><th className="table-th">Concepto</th><th className="table-th">Presupuestado</th><th className="table-th">Real</th><th className="table-th">Var.</th><th className="table-th"></th></tr></thead>
        <tbody>
          {presupuestos.map((p) => {
            const variacion = p.valorPresupuestado ? ((p.valorReal - p.valorPresupuestado) / p.valorPresupuestado) * 100 : 0;
            return (
              <tr key={p.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{p.periodo}</td>
                <td className="table-td font-medium">{p.concepto}</td>
                <td className="table-td">${p.valorPresupuestado.toLocaleString()}</td>
                <td className="table-td">${p.valorReal.toLocaleString()}</td>
                <td className="table-td"><span className={`badge ${variacion <= 0 ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{variacion >= 0 ? "+" : ""}{variacion.toFixed(1)}%</span></td>
                <td className="table-td text-right">{puedeEditar && <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => eliminar(p.id)}>Eliminar</button>}</td>
              </tr>
            );
          })}
          {presupuestos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin presupuestos registrados.</td></tr>}
        </tbody>
      </table>
      {puedeCrear && (
        <form onSubmit={crear} className="grid grid-cols-4 gap-2 p-4 border-t border-borderc">
          <input className="input-field" placeholder="Periodo (2026-09)" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required />
          <input className="input-field col-span-2" placeholder="Concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} required />
          <input className="input-field" type="number" step="0.01" placeholder="Valor presupuestado" value={valorPresupuestado} onChange={(e) => setValorPresupuestado(e.target.value)} required />
          <button className="btn-secondary text-xs col-span-4" disabled={enviando}>+ Agregar presupuesto</button>
        </form>
      )}
    </div>
  );
}

function TabFlujo({ flujo, puedeCrear, recargar }: { flujo: FlujoCaja[]; puedeCrear: boolean; recargar: () => void }) {
  const [fecha, setFecha] = useState("");
  const [tipo, setTipo] = useState("ingreso");
  const [concepto, setConcepto] = useState("");
  const [valor, setValor] = useState("");
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("/gerencial/flujo-caja", { fecha, tipo, concepto, valor: Number(valor) });
      setFecha(""); setConcepto(""); setValor("");
      recargar();
    } catch { /* noop */ } finally { setEnviando(false); }
  };

  const totalIngresos = flujo.filter((f) => f.tipo === "ingreso").reduce((s, f) => s + f.valor, 0);
  const totalEgresos = flujo.filter((f) => f.tipo === "egreso").reduce((s, f) => s + f.valor, 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="card p-4"><div className="text-xs text-textmuted">Ingresos</div><div className="text-lg font-bold text-accent-dark">${totalIngresos.toLocaleString()}</div></div>
        <div className="card p-4"><div className="text-xs text-textmuted">Egresos</div><div className="text-lg font-bold text-red-600">${totalEgresos.toLocaleString()}</div></div>
        <div className="card p-4"><div className="text-xs text-textmuted">Saldo neto</div><div className="text-lg font-bold text-slate-800">${(totalIngresos - totalEgresos).toLocaleString()}</div></div>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">Tipo</th><th className="table-th">Concepto</th><th className="table-th">Valor</th></tr></thead>
          <tbody>
            {flujo.map((f) => (
              <tr key={f.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{f.fecha}</td>
                <td className="table-td"><span className={`badge ${f.tipo === "ingreso" ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>{f.tipo}</span></td>
                <td className="table-td">{f.concepto}</td>
                <td className="table-td font-semibold">${f.valor.toLocaleString()}</td>
              </tr>
            ))}
            {flujo.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-8">Sin movimientos de caja.</td></tr>}
          </tbody>
        </table>
        {puedeCrear && (
          <form onSubmit={crear} className="grid grid-cols-5 gap-2 p-4 border-t border-borderc">
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="ingreso">Ingreso</option><option value="egreso">Egreso</option>
            </select>
            <input className="input-field col-span-2" placeholder="Concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} required />
            <input className="input-field" type="number" step="0.01" placeholder="Valor" value={valor} onChange={(e) => setValor(e.target.value)} required />
            <button className="btn-secondary text-xs col-span-5" disabled={enviando}>+ Registrar movimiento</button>
          </form>
        )}
      </div>
    </div>
  );
}

function TabRiesgos({ riesgos, puedeCrear, puedeEditar, recargar }: { riesgos: Riesgo[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [probabilidad, setProbabilidad] = useState("media");
  const [impacto, setImpacto] = useState("medio");
  const [controlForm, setControlForm] = useState<Record<number, string>>({});
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("/gerencial/riesgos", { descripcion, probabilidad, impacto });
      setModalAbierto(false);
      setDescripcion(""); setProbabilidad("media"); setImpacto("medio");
      recargar();
    } catch { /* noop */ } finally { setEnviando(false); }
  };

  const cambiarEstado = async (r: Riesgo, estado: string) => {
    try { await api.put(`/gerencial/riesgos/${r.id}`, { estado }); recargar(); } catch { /* noop */ }
  };

  const agregarControl = async (riesgoId: number, e: React.FormEvent) => {
    e.preventDefault();
    const descripcionControl = controlForm[riesgoId];
    if (!descripcionControl) return;
    try {
      await api.post(`/gerencial/riesgos/${riesgoId}/controles`, { descripcionControl });
      setControlForm((prev) => ({ ...prev, [riesgoId]: "" }));
      recargar();
    } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo riesgo</button>}
      </div>
      <div className="space-y-4">
        {riesgos.map((r) => (
          <div key={r.id} className="card p-5">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm font-medium text-slate-800 max-w-xl">{r.descripcion}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="badge bg-slate-100 text-slate-600">prob. {r.probabilidad}</span>
                <span className="badge bg-slate-100 text-slate-600">impacto {r.impacto}</span>
                <span className={`badge ${r.estado === "cerrado" ? "bg-accent-light text-accent-dark" : r.estado === "mitigado" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{r.estado}</span>
              </div>
            </div>
            <ul className="text-xs text-textmuted space-y-1 mb-2">
              {r.controles.map((c) => <li key={c.id}>· {c.descripcionControl}{c.responsable ? ` — ${c.responsable}` : ""}</li>)}
              {r.controles.length === 0 && <li>Sin controles mitigantes definidos.</li>}
            </ul>
            {puedeEditar && (
              <div className="flex items-center gap-2 border-t border-borderc pt-3">
                <form onSubmit={(e) => agregarControl(r.id, e)} className="flex-1 flex gap-2">
                  <input className="input-field text-xs" placeholder="Nuevo control mitigante" value={controlForm[r.id] || ""} onChange={(e) => setControlForm((prev) => ({ ...prev, [r.id]: e.target.value }))} />
                  <button className="btn-ghost text-xs">+ Control</button>
                </form>
                {r.estado !== "cerrado" && (
                  <select className="input-field text-xs w-32" value={r.estado} onChange={(e) => cambiarEstado(r, e.target.value)}>
                    <option value="abierto">Abierto</option><option value="mitigado">Mitigado</option><option value="cerrado">Cerrado</option>
                  </select>
                )}
              </div>
            )}
          </div>
        ))}
        {riesgos.length === 0 && <div className="card p-6 text-sm text-textmuted text-center">Sin riesgos identificados.</div>}
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo riesgo</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
              <textarea className="input-field" rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Probabilidad</label>
                <select className="input-field" value={probabilidad} onChange={(e) => setProbabilidad(e.target.value)}>
                  <option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option>
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Impacto</label>
                <select className="input-field" value={impacto} onChange={(e) => setImpacto(e.target.value)}>
                  <option value="bajo">Bajo</option><option value="medio">Medio</option><option value="alto">Alto</option>
                </select></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabHallazgos({ hallazgos, puedeCrear, puedeEditar, recargar }: { hallazgos: Hallazgo[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modulo, setModulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [severidad, setSeveridad] = useState("media");
  const [seguimientoForm, setSeguimientoForm] = useState<Record<number, string>>({});
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("/gerencial/hallazgos", { modulo, descripcion, severidad });
      setModalAbierto(false);
      setModulo(""); setDescripcion(""); setSeveridad("media");
      recargar();
    } catch { /* noop */ } finally { setEnviando(false); }
  };

  const cerrar = async (h: Hallazgo) => {
    try { await api.put(`/gerencial/hallazgos/${h.id}`, { estado: "cerrado" }); recargar(); } catch { /* noop */ }
  };

  const agregarSeguimiento = async (hallazgoId: number, e: React.FormEvent) => {
    e.preventDefault();
    const accion = seguimientoForm[hallazgoId];
    if (!accion) return;
    try {
      await api.post(`/gerencial/hallazgos/${hallazgoId}/seguimientos`, { fecha: new Date().toISOString().slice(0, 10), accion });
      setSeguimientoForm((prev) => ({ ...prev, [hallazgoId]: "" }));
      recargar();
    } catch { /* noop */ }
  };

  const SEVERIDAD_BADGE: Record<string, string> = { baja: "bg-slate-100 text-slate-500", media: "bg-amber-50 text-amber-700", alta: "bg-orange-50 text-orange-700", critica: "bg-red-50 text-red-700" };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo hallazgo</button>}
      </div>
      <div className="space-y-4">
        {hallazgos.map((h) => (
          <div key={h.id} className="card p-5">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="badge bg-navy/10 text-navy mr-2">{h.modulo}</span>
                <span className="text-sm text-slate-800">{h.descripcion}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`badge ${SEVERIDAD_BADGE[h.severidad]}`}>{h.severidad}</span>
                <span className={`badge ${h.estado === "cerrado" ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-600"}`}>{h.estado}</span>
              </div>
            </div>
            <ul className="text-xs text-textmuted space-y-1 mb-2">
              {h.seguimientos.map((s) => <li key={s.id}>· {s.fecha} — {s.accion}{s.responsable ? ` (${s.responsable})` : ""}</li>)}
              {h.seguimientos.length === 0 && <li>Sin seguimientos registrados.</li>}
            </ul>
            {puedeEditar && h.estado !== "cerrado" && (
              <div className="flex items-center gap-2 border-t border-borderc pt-3">
                <form onSubmit={(e) => agregarSeguimiento(h.id, e)} className="flex-1 flex gap-2">
                  <input className="input-field text-xs" placeholder="Nueva acción de seguimiento" value={seguimientoForm[h.id] || ""} onChange={(e) => setSeguimientoForm((prev) => ({ ...prev, [h.id]: e.target.value }))} />
                  <button className="btn-ghost text-xs">+ Seguimiento</button>
                </form>
                <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => cerrar(h)}>Cerrar hallazgo</button>
              </div>
            )}
          </div>
        ))}
        {hallazgos.length === 0 && <div className="card p-6 text-sm text-textmuted text-center">Sin hallazgos registrados.</div>}
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo hallazgo</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
              <input className="input-field" value={modulo} onChange={(e) => setModulo(e.target.value)} placeholder="ej. Inventarios" required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
              <textarea className="input-field" rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Severidad</label>
              <select className="input-field" value={severidad} onChange={(e) => setSeveridad(e.target.value)}>
                <option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option><option value="critica">Crítica</option>
              </select></div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
